import { useState } from "react";

import clsx from "clsx";
import Interweave from "interweave";

import { InterleaveSpans } from "@components/utilities";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { jsonHash } from "@north-beam/nb-common";

import { type TemplateDetail } from "../types";

interface Props {
  templateDetails: TemplateDetail[];
}

export const OutputValueTemplates = ({ templateDetails }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-2 rounded-lg">
      <button
        className="py-3 px-6 flex items-center justify-between w-full"
        onClick={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Toggle output value template",
            data: {
              newIsExpanded: !isExpanded,
              pageContext: { isExpanded },
            },
          });

          setIsExpanded(!isExpanded);
        }}
      >
        <div>Templates for output value</div>
        <i
          className={clsx(
            "fa-regular font-bold text-cosmic",
            isExpanded ? "fa-chevron-up" : "fa-chevron-down",
          )}
        />
      </button>
      {isExpanded && (
        <div className="pb-4 px-6 mt-2">
          <div className="border border-gray rounded-lg overflow-auto">
            <table className="text-xs table-fixed	border-spacing-0 border-separate min-w-[100%]">
              <thead>
                <tr>
                  <th className="h-[36px] w-[120px] min-w-[120px] border-b border-gray-2">
                    <div className="w-[220px] min-w-[220px] flex items-center pl-2 gap-2 h-full">
                      <div className="flex-1">Description</div>
                      <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
                    </div>
                  </th>
                  <th className="h-[36px] border-b border-gray-2">
                    <div className="flex items-center pl-2 gap-2 h-full">
                      <div className="flex-1">Template values</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {templateDetails.map(({ description, templates }, index) => (
                  <tr
                    key={jsonHash({ description, index })}
                    className="group odd:bg-[#fafafe] even:bg-white"
                  >
                    <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                      <div className="font-bold p-2 text-sm">
                        <Interweave content={description} />
                      </div>
                    </td>
                    <td className="border-gray-2 align-top border-b group-last:border-b-0">
                      <div className="p-2 text-sm">
                        {templates.length > 0 ? (
                          <InterleaveSpans
                            text={templates.map((v) => `{{${v}}}`)}
                            sep=", "
                            Wrapper={(props: any) => (
                              <code {...props} className="text-venus" />
                            )}
                          />
                        ) : (
                          <span className="text-muted">(none)</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="group odd:bg-[#fafafe] even:bg-white">
                  <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                    <div className="font-bold p-2 text-sm">Functions</div>
                  </td>
                  <td className="border-gray-2 align-top border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span>Lowercasing:</span>
                        <code className="text-venus">
                          {"{{#functions.lower}}Howdy{{/functions.lower}}"}
                        </code>
                        <i className="fa-regular fa-arrow-right text-xs" />
                        <code className="text-venus">howdy</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>Uppercasing:</span>
                        <code className="text-venus">
                          {"{{#functions.upper}}Hello{{/functions.upper}}"}
                        </code>
                        <i className="fa-regular fa-arrow-right text-xs" />
                        <code className="text-venus">HELLO</code>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
